import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHero, CtaBand, splitRows, CARD_MAX_W } from "@/components/blocks";
import { BrandImage } from "@/components/BrandImage";
import { Reveal } from "@/components/Reveal";
import { useQuote } from "@/components/QuoteModal";
import { COMPANY } from "@/data/site";
import { breadcrumbJsonLd } from "@/lib/seo";

const IndexCard = ({ card, index }) => {
  const { openQuote } = useQuote();
  const slug = card.title.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const body = (
    <>
      {card.img ? (
        <div className="relative overflow-hidden rounded-t-lg">
          <BrandImage src={card.img} imgKey={card.imgKey} alt={card.alt || card.title} className="img-zoom w-full h-44" testId={`index-card-img-${slug}`} />
          <div className="absolute inset-0 bg-gradient-to-t from-cts-ink/25 via-transparent to-transparent pointer-events-none" aria-hidden="true" />
        </div>
      ) : (
        <div className="h-44 rounded-t-lg bg-cts-soft flex items-center justify-center border-b border-cts-line">
          {card.icon && <card.icon size={44} strokeWidth={1.5} className="text-cts-red" />}
        </div>
      )}
      <div className="p-6">
        {card.badge && (
          <span className="inline-block font-heading font-bold text-[10px] tracking-[0.14em] uppercase bg-cts-redlight text-cts-red rounded px-2.5 py-1 mb-3">
            {card.badge}
          </span>
        )}
        <h2 className="font-heading font-bold text-lg text-cts-ink mb-2">{card.title}</h2>
        <p className="text-[15px] text-cts-charcoal leading-relaxed">{card.text}</p>
        <span className="link-arrow mt-4">
          {card.ctaLabel || (card.to ? "EXPLORE" : "ASK FOR PRICE")} <ArrowRight size={15} />
        </span>
      </div>
    </>
  );
  const cls = "card-base group block h-full overflow-hidden";
  return (
    <Reveal delay={index * 60}>
      {card.to ? (
        <Link to={card.to} data-testid={`index-card-${slug}`} className={cls}>{body}</Link>
      ) : (
        <button onClick={() => openQuote(card.quoteReq)} data-testid={`index-card-${slug}`} className={`${cls} text-left w-full`}>{body}</button>
      )}
    </Reveal>
  );
};

export default function IndexPage({ config }) {
  const cols = config.cols || 3;
  const colClass = cols === 4 ? "lg:grid-cols-4" : cols === 5 ? "lg:grid-cols-5" : "lg:grid-cols-3";
  const [mainCards, lastRow] = splitRows(config.cards, cols);
  const crumbs = [{ label: config.breadcrumbLabel || config.h1 }];
  return (
    <>
      <Seo title={config.metaTitle} siteName={COMPANY.brand} description={config.metaDesc} jsonLd={breadcrumbJsonLd(crumbs)} />
      <PageHero eyebrow={config.eyebrow} title={config.h1} intro={config.intro} crumbs={crumbs} />
      <section className="section-pad">
        <div className="container-site">
          <div className={`grid gap-6 sm:grid-cols-2 ${colClass}`}>
            {mainCards.map((card, i) => (
              <IndexCard key={card.title} card={card} index={i} />
            ))}
          </div>
          {lastRow.length > 0 && (
            <div className="flex flex-wrap justify-center gap-6 mt-6">
              {lastRow.map((card, i) => (
                <div key={card.title} className="w-full sm:w-[calc(50%-12px)]" style={{ maxWidth: CARD_MAX_W[cols] }}>
                  <IndexCard card={card} index={mainCards.length + i} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      {config.cta && <CtaBand {...config.cta} />}
    </>
  );
}
